//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Icon, Select, SelectItem } from 'unison';

import { instagramAccountActions } from '@instagram-account/redux/account/instagramAccountActions';
import { requestRightsActions } from '@legacy-components/rights-management/request-rights-actions';
import { ICuralateStore } from '@models/AppState';
import styled from 'styled-components';
import { ReduxDispatch } from 'app/curalate.store';
import Avatar from '@components/atoms/avatar/Avatar';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { personalizeRightsRequestMessage } from '@legacy-components/rights-management/request-rights-util';
import { getIsIncludeUsernameEnabled } from '@galleries/selectors/bulk-rights-selectors/getRightsPersonalizationFeatures';
import { getTikTokAccountSettings } from '@tiktok/redux/account/selectors/getTikTokAccountSettings';
import { getTikTokUser } from '@tiktok/redux/user/selectors/getTikTokUser';
import { fetchUser } from '@tiktok/redux/user/tiktokUserSlice';
import { SocialNetwork } from '@models/SocialNetwork';
import {
  getTikTokSettings,
  connectTikTok,
} from '@account-settings/redux/tiktok/tiktokSettingsSlice';
import { GENERAL_MESSAGING_TIKTOK } from '@account-settings/components/rights-requests/constants/rightRequestConstants';

const mapStateToProps = (state: ICuralateStore) => {
  const tikTokAccountData = getTikTokAccountSettings(state);
  const {
    isConnected: isTikTokConnected = false,
    flags: tikTokFlags = { isFetchingSettings: false },
  } = tikTokAccountData || {};

  const tikTokUser = getTikTokUser(state);

  return {
    requestRights: state.requestRights,
    session: state.session,
    instagram: state.instagram.account,
    isTikTokConnected,
    tikTokFlags,
    tikTokUser,

    // TODO: PD-249308 - remove rights_requests_include_username flag
    isIncludeUsernameEnabled: getIsIncludeUsernameEnabled(state),
  };
};

const mapDispatchToProps = (dispatch: ReduxDispatch) => {
  return {
    actions: {
      requestRights: bindActionCreators(requestRightsActions, dispatch),
      instagram: bindActionCreators(instagramAccountActions, dispatch),
      tikTokSettings: bindActionCreators(
        {
          getTikTokSettings,
          connectTikTok,
        },
        dispatch,
      ),
      tikTokUser: bindActionCreators(
        {
          fetchUser,
        },
        dispatch,
      ),
    },
  };
};

interface IOwnProps {
  className?: string;
  showLoadingFiller?: boolean;
  selectedItem?: IContentLibraryItem;
}

interface IOwnState {
  isLoaded: boolean;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  IOwnProps;

export class RotatingRightsMessages extends React.Component<Props, IOwnState> {
  public static defaultProps = {
    showLoadingFiller: true,
  };

  public state = {
    isLoaded: false,
  };

  private isTikTokItem = (): boolean => {
    return (
      this.props.selectedItem?.socialDetails?.socialNetwork ===
      SocialNetwork.TikTok
    );
  };

  private findTikTokConfig = () => {
    const {
      requestRights: {
        configurations: { byId },
      },
    } = this.props;

    return Object.entries(byId).find(([, config]) => {
      if (!config?.name) return false;
      const configName = config.name.toLowerCase();
      return configName === GENERAL_MESSAGING_TIKTOK.toLowerCase();
    });
  };

  public async componentDidMount() {
    const {
      actions: {
        instagram: { fetchDefaultInstagramAccount },
        requestRights: { requestRightsConfigurations },
        tikTokSettings: { getTikTokSettings },
        tikTokUser: { fetchUser },
      },
      instagram: { defaultInstagramAccount },
      requestRights: { areConfigurationsFetched },
      isTikTokConnected,
      tikTokFlags,
      tikTokUser,
    } = this.props;

    const loadingPromises = [];

    if (!defaultInstagramAccount) {
      loadingPromises.push(fetchDefaultInstagramAccount());
    }

    if (!areConfigurationsFetched) {
      loadingPromises.push(requestRightsConfigurations());
    }

    if (!isTikTokConnected && !tikTokFlags.isFetchingSettings) {
      loadingPromises.push(getTikTokSettings());
    }

    if (isTikTokConnected && !tikTokUser) {
      loadingPromises.push(fetchUser());
    }

    await Promise.all(loadingPromises);

    this.setState({ isLoaded: true });
  }

  public render() {
    const {
      requestRights: {
        configurations: { byId },
        selectedConfigurationId,
      },
      instagram: { defaultInstagramAccount },
      session: { client },
      className,
      showLoadingFiller,
    } = this.props;

    if (!this.state.isLoaded) {
      return showLoadingFiller ? this.getLoadingFiller() : null;
    }

    const rightsConfigurations = this.getRightsConfigurations();
    const brandDetails = this.getBrandDetails();
    const selectedValue = this.getSelectedConfigurationValue();
    const selectKey =
      this.props.selectedItem?.socialDetails?.socialNetwork || 'default';
    return (
      <div className={className}>
        <Select
          key={selectKey}
          handleChange={this.handleSelectChange}
          items={rightsConfigurations}
          value={selectedValue}
          className='cu-w-100 select-popover-scroll'
        >
          {rightsConfigurations.map(({ label, value }) => (
            <SelectItem key={label} label={label} value={value} />
          ))}
        </Select>
        <RotatingRightsRequests>
          <RefreshIcon name='sync-alt' onClick={this.handleMessageChange} />
          <RotatingRightsRequestsContent>
            {brandDetails.icon}
            <div className='cu-flex-1 cu-text-body'>
              <span className='cu-text-body-2'>
                {brandDetails.username}
                &nbsp;
              </span>
              {this.getCurrentRightsMessage()}
            </div>
          </RotatingRightsRequestsContent>
        </RotatingRightsRequests>
      </div>
    );
  }

  private getLoadingFiller = () => {
    const { className } = this.props;

    return (
      <div className={className}>
        <SelectLoadingFiller className='loading-element gradient' />
        <MessageLoadingFiller className='loading-element gradient' />
      </div>
    );
  };

  private handleSelectChange = (configurationValue: string) => {
    const {
      actions: {
        requestRights: { selectConfigurationId, selectMessageIndex },
      },
    } = this.props;

    let configurationId = parseInt(configurationValue, 10);

    if (this.isTikTokItem()) {
      const tikTokConfig = this.findTikTokConfig();
      if (tikTokConfig) {
        configurationId = parseInt(tikTokConfig[0], 10);
      }
    }

    selectConfigurationId(configurationId);
    selectMessageIndex(0);
  };

  private handleMessageChange = () => {
    const {
      requestRights: {
        configurations: { byId },
        selectedConfigurationId,
        selectedMessageIndex,
      },
      actions: {
        requestRights: { selectMessageIndex },
      },
    } = this.props;

    const configuration = byId[selectedConfigurationId];
    if (!configuration?.rightsRequestData?.data) {
      return;
    }

    const numMessages = configuration.rightsRequestData.data.length;
    selectMessageIndex((selectedMessageIndex + 1) % numMessages);
  };

  private getCurrentRightsMessage = () => {
    const {
      requestRights: {
        configurations: { byId },
        selectedConfigurationId,
        selectedMessageIndex,
      },
      selectedItem,
      isIncludeUsernameEnabled,
    } = this.props;

    let configuration = byId[selectedConfigurationId];

    // If TikTok content, use the General messaging TikTok campaign
    if (this.isTikTokItem()) {
      const tikTokConfig = this.findTikTokConfig();
      if (tikTokConfig) {
        configuration = tikTokConfig[1];
      }
    }

    if (!configuration?.rightsRequestData?.data?.[selectedMessageIndex]) {
      return '';
    }

    const message =
      configuration.rightsRequestData.data[selectedMessageIndex].message;

    return personalizeRightsRequestMessage(
      isIncludeUsernameEnabled,
      message,
      selectedItem?.socialDetails?.user?.username ?? '',
    );
  };

  private getBrandDetails = () => {
    const {
      session: { client },
      instagram: { defaultInstagramAccount },
      isTikTokConnected,
      tikTokUser,
    } = this.props;

    if (this.isTikTokItem() && isTikTokConnected) {
      const tikTokUserName =
        tikTokUser?.displayName || tikTokUser?.username || client.name;
      return {
        username: tikTokUserName,
        icon: (
          <Avatar
            size={48}
            imageUrl={tikTokUser?.profileImage}
            username={tikTokUserName}
            className='cu-mr-2'
          />
        ),
      };
    }

    const username = defaultInstagramAccount
      ? defaultInstagramAccount.username
      : client.name;
    const avatarProps = defaultInstagramAccount
      ? { imageUrl: defaultInstagramAccount.profileImageUrl }
      : { username: client.name };

    return {
      username,
      icon: <Avatar size={48} {...avatarProps} className='cu-mr-2' />,
    };
  };

  private getSelectedConfigurationValue = () => {
    const {
      requestRights: {
        configurations: { byId },
        selectedConfigurationId,
      },
    } = this.props;

    if (this.isTikTokItem()) {
      const tikTokConfig = this.findTikTokConfig();
      return (
        tikTokConfig?.[0] ??
        byId[selectedConfigurationId]?.id?.toString(10) ??
        ''
      );
    }
    return byId[selectedConfigurationId]?.id?.toString(10) ?? '';
  };

  private getRightsConfigurations = () => {
    const {
      requestRights: {
        configurations: { byId },
      },
    } = this.props;

    if (this.isTikTokItem()) {
      const tikTokConfig = this.findTikTokConfig();

      if (tikTokConfig) {
        const [, configuration] = tikTokConfig;
        return [
          {
            label: configuration!.name,
            value: `${configuration!.id}`,
          },
        ];
      }
    }

    return Object.entries(byId)
      .filter(([, configuration]) => configuration != null)
      .map(([configurationId, configuration]) => ({
        label: configuration!.name,
        value: `${configurationId}`,
      }));
  };
}

const RotatingRightsRequests = styled.div`
  background-color: ${({ theme }) => theme.colors.gray00};
  margin-top: ${({ theme }) => theme.spacings[2]};
  position: relative;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.gray3};
  padding: ${({ theme }) =>
    `${theme.spacings[3]} ${theme.spacings[6]} ${theme.spacings[3]} ${theme.spacings[3]}`};
`;

const RefreshIcon = styled(Icon)`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;

  .cu-icon {
    width: 10px;
    height: 10px;
  }
`;

const RotatingRightsRequestsContent = styled.div`
  display: flex;
  align-items: start;
  font-size: 12px;
  height: ${({ theme }) => theme.spacings[7]};
  overflow-y: auto;
`;

const MessageLoadingFiller = styled.div`
  width: 100%;
  ${({ theme }) => `
    height: ${theme.spacings[7]};
    margin-top: ${theme.spacings[2]};
  `}
`;

const SelectLoadingFiller = styled.div`
  width: 100%;
  height: ${({ theme }) => theme.spacings[5]};
`;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RotatingRightsMessages);
